/* eslint-disable react-hooks/rules-of-hooks, react-hooks/exhaustive-deps */

import React, { useEffect } from 'react';
import { AgentDetailsProps } from './AgentDetails.types';
import { ArrowheadRightSmall } from '@blueprint/icons';

import {
    AgentDetailsContainer,
    AgentCTAs,
    AgentContact,
    AgentProperties,
    AgentNumber,
    AgentImage,
    AgentName,
    AgentHeader,
    AgentCallCTA,
    AgentImageLink
} from './AgentDetails.styles';
import { getCorrectType } from './AgentDetails.helpers';
import {
    ButtonCall,
    ButtonEmail
} from 'listing-details/components/Buttons/Buttons';
import { UserActions } from 'listing-details/components/UserActions/UserActions';

import {
    trackTopAgentImage,
    trackViewAgentPropertiesLink,
    trackEmailAgentCTA,
    trackCallAgentCTA,
    trackCallAgentCTADesktop,
    trackCallAgentSplitTest
} from './AgentDetails.tracking';

import { useSelector } from 'react-redux';
import {
    withOptimizely,
    WithOptimizelyProps,
    selectForcedFeatures,
    getOptimizelyFeatureFlag
} from 'shared/optimizely';

import AgentCallCTAVariant from './AgentCallCTAVariant/AgentCallCTAVariant';

export const AgentDetailsOptimizely = ({
    optimizely,
    ...props
}: AgentDetailsProps & { contactPath: string } & WithOptimizelyProps) => {
    // Click to reveal agent number split test
    const featureFlagOverrides = useSelector(selectForcedFeatures);
    const isCallAgentSplitTest = getOptimizelyFeatureFlag(
        'lex-595-call-agent-click',
        featureFlagOverrides,
        optimizely
    );

    useEffect(() => {
        trackCallAgentSplitTest(isCallAgentSplitTest ? 'variant' : 'original');
    }, [props.listingId]);

    return (
        <AgentDetails {...props} isCallAgentSplitTest={isCallAgentSplitTest} />
    );
};

export const AgentDetails = ({
    listingId,
    data,
    openModal,
    section,
    contactPath,
    isCallAgentSplitTest
}: AgentDetailsProps & {
    contactPath: string;
    isCallAgentSplitTest: boolean;
}) => {
    if (!data?.branch) {
        return null;
    }

    const {
        branch: {
            branchDetailsUri,
            branchResultsUri,
            logoUrl,
            phone,
            name,
            memberType
        },
        analyticsTaxonomy
    } = data;

    const type = getCorrectType(memberType);
    const isDeveloper = type === 'developer';

    const propertyAvailableLink = branchResultsUri || branchDetailsUri;
    const propertyLink = isDeveloper ? branchDetailsUri : propertyAvailableLink;

    const textLink = isDeveloper
        ? 'View this development'
        : `View ${type} properties`;

    const emailCTA = isDeveloper ? 'Email developer' : 'Email agent';
    const callCTA = isDeveloper ? 'Call developer' : 'Call agent';

    return (
        <>
            <AgentDetailsContainer data-testid="agent-details">
                <AgentHeader>
                    <AgentName size="6">
                        {name}
                        {propertyLink ? (
                            <AgentProperties
                                href={propertyLink}
                                data-testid="agent-properties-link"
                                onClick={trackViewAgentPropertiesLink}
                            >
                                {textLink} <ArrowheadRightSmall />
                            </AgentProperties>
                        ) : (
                            <></>
                        )}
                    </AgentName>
                    {logoUrl ? (
                        <AgentImageLink
                            href={branchDetailsUri || undefined}
                            data-testid="agent-image-link"
                            onClick={trackTopAgentImage}
                        >
                            <AgentImage
                                src={logoUrl}
                                alt={`Logo of ${name}`}
                                lazy={false}
                            />
                        </AgentImageLink>
                    ) : (
                        <></>
                    )}
                </AgentHeader>

                <AgentContact>
                    {isCallAgentSplitTest && phone ? (
                        <AgentCallCTAVariant
                            taxonomy={analyticsTaxonomy}
                            listingId={listingId}
                            phone={phone}
                        />
                    ) : (
                        <AgentCallCTA>
                            <strong>Call {type}:</strong>
                            {phone ? (
                                <AgentNumber
                                    href={`tel: ${phone}`}
                                    data-testid="agent-phone-cta-link"
                                    onClick={trackCallAgentCTADesktop}
                                >
                                    {phone}
                                </AgentNumber>
                            ) : (
                                <></>
                            )}
                        </AgentCallCTA>
                    )}

                    <AgentCTAs>
                        <ButtonEmail
                            onClick={trackEmailAgentCTA}
                            data-testid="agent-email-cta"
                            href={contactPath}
                        >
                            {emailCTA}
                        </ButtonEmail>
                        <ButtonCall
                            href={`tel:${phone}`}
                            onClick={() =>
                                trackCallAgentCTA({
                                    listingId,
                                    taxonomy: analyticsTaxonomy
                                })
                            }
                            data-testid="agent-phone-cta"
                        >
                            {callCTA}
                        </ButtonCall>
                    </AgentCTAs>
                </AgentContact>
                <UserActions
                    listingId={listingId}
                    openModal={openModal}
                    section={section}
                />
            </AgentDetailsContainer>
        </>
    );
};

export default withOptimizely(AgentDetailsOptimizely);
